import React, { useState, useEffect } from 'react';
import {
  UserIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { apiRequest } from '../../utils/apiUtils';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * AssignmentSelector Component
 *
 * Provides a simple selector for assigning issues during creation.
 * Features:
 * - Search for users and teams
 * - Select either a user OR a team (mutually exclusive)
 * - Clear selection
 */
const AssignmentSelector = ({ value, onChange }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);

  // Initialize from value prop
  useEffect(() => {
    if (value) {
      setSelectedEntity(value);
    }
  }, [value]);

  // Search for users and teams
  useEffect(() => {
    const searchEntities = async () => {
      if (!searchQuery || searchQuery.length < 1) {
        setSearchResults([]);
        return;
      }

      try {
        setLoading(true);

        // Search for users
        const userResponse = await apiRequest(`/profiles?search=${encodeURIComponent(searchQuery)}&limit=10`);
        const users = userResponse.success ? (userResponse.users || []) : [];

        // Search for teams
        const teamResponse = await apiRequest(`/teams?search=${encodeURIComponent(searchQuery)}&limit=10`);
        const teams = teamResponse.success ? (teamResponse.teams || []) : [];

        // Combine results with type indicators
        const combinedResults = [
          ...users.map(u => ({ ...u, type: 'user' })),
          ...teams.map(t => ({ ...t, type: 'team' }))
        ];

        setSearchResults(combinedResults);
      } catch (error) {
        console.error('Error searching for assignment targets:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      searchEntities();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleSelect = (entity) => {
    setSelectedEntity(entity);
    setSearchQuery('');
    setSearchResults([]);

    // Notify parent component
    onChange(entity);
  };

  const handleClear = () => {
    setSelectedEntity(null);
    setSearchQuery('');
    setSearchResults([]);
    onChange(null);
  };

  return (
    <div className="space-y-2">
      {/* Current Selection */}
      {selectedEntity && (
        <div className="flex items-center justify-between bg-primary-50 border border-primary-200 rounded-md px-3 py-2">
          <div className="flex items-center gap-2">
            {selectedEntity.type === 'user' ? (
              <>
                <UserIcon className="h-4 w-4 text-primary-600" />
                <span className="text-sm font-medium text-primary-900">
                  {selectedEntity.first_name && selectedEntity.last_name
                    ? `${selectedEntity.first_name} ${selectedEntity.last_name}`
                    : selectedEntity.email}
                </span>
                {selectedEntity.first_name && selectedEntity.last_name && (
                  <span className="text-xs text-primary-600">({selectedEntity.email})</span>
                )}
              </>
            ) : (
              <>
                <UserGroupIcon className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-medium text-purple-900">{selectedEntity.name}</span>
              </>
            )}
          </div>
          <button
            onClick={handleClear}
            className="text-secondary-400 hover:text-secondary-600"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Search Input */}
      {!selectedEntity && (
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search users or teams..."
            className="w-full pl-10 pr-3 py-2 border border-secondary-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      )}

      {/* Search Results */}
      {!selectedEntity && searchQuery && (
        <div className="max-h-64 overflow-y-auto bg-white border border-secondary-200 rounded-md shadow-sm">
          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="sm" />
            </div>
          ) : searchResults.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-secondary-500">No users or teams found</p>
            </div>
          ) : (
            <div className="py-2">
              {searchResults.map((result) => (
                <button
                  key={`${result.type}-${result.id}`}
                  onClick={() => handleSelect(result)}
                  className="w-full text-left px-4 py-2 hover:bg-secondary-50 transition-colors flex items-center"
                >
                  {result.type === 'user' ? (
                    <>
                      {result.avatar_url ? (
                        <img
                          src={result.avatar_url}
                          alt=""
                          className="h-6 w-6 rounded-full mr-3"
                        />
                      ) : (
                        <div className="h-6 w-6 rounded-full bg-secondary-200 flex items-center justify-center mr-3">
                          <UserIcon className="h-4 w-4 text-secondary-600" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-secondary-900 truncate">
                          {result.first_name && result.last_name
                            ? `${result.first_name} ${result.last_name}`
                            : result.email}
                        </p>
                        {result.first_name && result.last_name && (
                          <p className="text-xs text-secondary-500 truncate">{result.email}</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div
                        className="h-6 w-6 rounded mr-3 flex items-center justify-center"
                        style={{ backgroundColor: result.color || '#6B7280' }}
                      >
                        <UserGroupIcon className="h-4 w-4 text-white" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-secondary-900 truncate">{result.name}</p>
                        {result.description && (
                          <p className="text-xs text-secondary-500 truncate">{result.description}</p>
                        )}
                      </div>
                    </>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AssignmentSelector;
